import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import type { AppRouter } from "@webtemplate/api/trpc";

export const trpc = createTRPCReact<AppRouter>();

/**
 * Module-level access token storage.
 * AuthProvider calls setAccessToken() to keep this in sync with auth state.
 */
let currentAccessToken: string | null = null;

/**
 * Set the current access token (called by AuthProvider on login/refresh/logout).
 */
export function setAccessToken(token: string | null): void {
  currentAccessToken = token;
}

/**
 * Get the current access token (for use in header injection).
 */
export function getAccessToken(): string | null {
  return currentAccessToken;
}

/**
 * Read the CSRF token from the csrf-token cookie (double-submit pattern).
 */
function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrf-token=([^;]+)/);
  return match?.[1] ?? "";
}

/**
 * Flag to prevent concurrent refresh attempts.
 */
let refreshInProgress: Promise<string | null> | null = null;

/**
 * Callback for performing token refresh.
 * Injected by AuthProvider to avoid circular dependency.
 */
let onRefreshToken: (() => Promise<string | null>) | null = null;

/**
 * Register the refresh callback (called once by AuthProvider on mount).
 */
export function setRefreshHandler(handler: () => Promise<string | null>): void {
  onRefreshToken = handler;
}

/**
 * Clear the refresh callback (called by AuthProvider on unmount).
 * Prevents stale callbacks from being invoked after the provider is unmounted.
 */
export function clearRefreshHandler(): void {
  onRefreshToken = null;
}

/**
 * Attempt to refresh the access token.
 * Returns the new access token or null if refresh failed.
 * Deduplicates concurrent refresh attempts.
 */
async function attemptRefresh(): Promise<string | null> {
  if (!onRefreshToken) return null;

  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = onRefreshToken().finally(() => {
    refreshInProgress = null;
  });

  return refreshInProgress;
}

/**
 * Check if a tRPC error is an UNAUTHORIZED error (401).
 */
function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    return error.data?.code === "UNAUTHORIZED";
  }
  return false;
}

export function createTRPCClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: "/api/trpc",
        headers() {
          const headers: Record<string, string> = {};

          // CSRF token on all requests (double-submit cookie pattern)
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            headers["X-CSRF-Token"] = csrfToken;
          }

          // Bearer auth token when authenticated
          if (currentAccessToken) {
            headers["Authorization"] = `Bearer ${currentAccessToken}`;
          }

          return headers;
        },
        fetch: async (url, options) => {
          // Include cookies for httpOnly refresh token
          const response = await fetch(url, { ...options, credentials: "include" });

          // Skip auto-refresh for auth.refresh and auth.logout endpoints to
          // prevent self-referential refresh loops / deadlocks (if refresh
          // itself returns 401, retrying would call refresh again infinitely).
          const urlStr = typeof url === "string" ? url : url.toString();
          // Skip auto-refresh for auth endpoints that intentionally return
          // UNAUTHORIZED (login, 2FA verify, etc.) -- only refresh when a
          // previously-authenticated request fails due to token expiry.
          const isAuthEndpoint =
            urlStr.includes("auth.refresh") ||
            urlStr.includes("auth.logout") ||
            urlStr.includes("auth.login") ||
            urlStr.includes("auth.register") ||
            urlStr.includes("auth.verifyTwoFactor") ||
            urlStr.includes("auth.verifyEmailOtp") ||
            urlStr.includes("auth.verifyBackupCode") ||
            urlStr.includes("auth.forgotPassword") ||
            urlStr.includes("auth.resetPassword");

          // Detect auth failures that need a token refresh.
          // Only auto-retry single-operation requests to avoid re-executing
          // successful mutations in a multi-operation batch (which could
          // duplicate side effects like payments, emails, etc.).
          let needsRefresh = false;
          let isSingleOp = false;

          if (response.status === 401) {
            // HTTP 401 -- always a candidate for refresh (single-op or non-batched)
            needsRefresh = true;
            isSingleOp = true; // 401 status means the entire request failed
          } else if (response.status === 200 && !isAuthEndpoint) {
            try {
              const cloned = response.clone();
              const body: unknown = await cloned.json();
              if (Array.isArray(body) && body.length === 1) {
                // Single-operation batch: safe to retry without duplicating side effects
                isSingleOp = true;
                const op = body[0] as Record<string, unknown>;
                needsRefresh = !!(
                  op?.error &&
                  (op.error as Record<string, unknown>)?.data &&
                  (op.error as Record<string, Record<string, unknown>>).data?.code ===
                    "UNAUTHORIZED"
                );
              }
              // Multi-operation batches (length > 1) are NOT retried to prevent
              // re-executing successful mutations alongside the failed one.
            } catch {
              // JSON parse failure -- not a tRPC batch response, skip
            }
          }

          if (needsRefresh && isSingleOp && onRefreshToken && !isAuthEndpoint) {
            const newToken = await attemptRefresh();
            if (newToken) {
              // Retry the original request with the new token
              const retryHeaders = new Headers(options?.headers);
              retryHeaders.set("Authorization", `Bearer ${newToken}`);
              return fetch(url, {
                ...options,
                headers: retryHeaders,
                credentials: "include",
              });
            }
          }

          return response;
        },
      }),
    ],
  });
}

// Re-export for error type checking in auth code
export { TRPCClientError };
export { isUnauthorizedError };
